import React, { useState, useEffect } from "react";
import { useNotix } from "../components/Notix/Notix";
import './ScheduledTaskBox.css';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  onSnapshot,
  Timestamp,
  deleteDoc
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

const ScheduledTaskBox = () => {
  const notix = useNotix();
  const [task, setTask] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [allTasks, setAllTasks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Nuevo estado para edición
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTask, setEditTask] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");

  // Obtener fecha local YYYY-MM-DD
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = getLocalDateString();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      setAllTasks([]);
      return;
    }

    const q = query(
      collection(db, "scheduledTasks"),
      where("userId", "==", user.uid)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const fetchedTasks = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllTasks(fetchedTasks);
    }, (error) => {
      console.error("Error en listener de tareas:", error);
    });

    return () => unsubscribe();
  }, [user]);

  // Filtrar solo tareas para hoy
  const todayTasks = allTasks.filter(t => t.date === today);

  const handleAddTask = async () => {
    if (!task) {
      notix.error("Escribe la descripción de la tarea", { position: "top-center" });
      return;
    }
    // Si no hay fecha, usar la de hoy por defecto
    const taskDate = date || today;
    if (taskDate < today) {
      notix.error("No puedes elegir una fecha pasada", { position: "top-center" });
      return;
    }
    if (!user) return;

    try {
      await addDoc(collection(db, "scheduledTasks"), {
        task,
        date: taskDate,
        time: time || "",
        userId: user.uid,
        completed: false, // <--- IMPORTANTE
        createdAt: Timestamp.now()
      });
      notix.success("Tarea añadida correctamente", {
        position: 'top-center'
      });
      setTask("");
      setDate("");
      setTime("");
      setShowForm(false);
    } catch (error) {
      console.error("Error añadiendo tarea:", error);
    }
  };

  // Marcar como completada o no completada
  const handleToggleCompleteTask = async (taskId, completed) => {
    try {
      await updateDoc(doc(db, "scheduledTasks", taskId), {
        completed: !completed
      });
      notix.info(
        !completed ? 'Tarea marcada como completada' : 'Tarea marcada como no completada',
        { position: 'top-center' }
      );
    } catch (error) {
      console.error("Error actualizando tarea:", error);
    }
  };

  // Guardar cambios de edición
  const handleSaveEdit = async (taskId) => {
    try {
      await updateDoc(doc(db, "scheduledTasks", taskId), {
        task: editTask,
        date: editDate,
        time: editTime
      });
      notix.success("Tarea editada correctamente", { position: "top-center" });
      setEditingTaskId(null);
      setEditTask("");
      setEditDate("");
      setEditTime("");
    } catch (error) {
      notix.error("Error editando tarea", { position: "top-center" });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const dateObj = new Date(dateStr);
    return dateObj.toLocaleDateString('es-ES', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "Sin hora específica";
    const [hours, minutes] = timeStr.split(':');
    const dateObj = new Date();
    dateObj.setHours(parseInt(hours), parseInt(minutes));
    return dateObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    if (!user || allTasks.length === 0) return;

    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 30);

    allTasks.forEach(async (t) => {
      if (t.date) {
        // t.date puede ser string "YYYY-MM-DD" o Timestamp
        let taskDate = t.date;
        if (typeof taskDate === "string") {
          taskDate = new Date(taskDate);
        } else if (taskDate.toDate) {
          taskDate = taskDate.toDate();
        }
        if (taskDate < weekAgo) {
          try {
            await deleteDoc(doc(db, "scheduledTasks", t.id));
          } catch (err) {
            console.error("Error borrando tarea antigua:", err);
          }
        }
      }
    });
  }, [allTasks, user]);

  if (loading) {
    return (
      <div className="task-box">
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <h4>Cargando...</h4>
          <p>Verificando autenticación</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="task-box">
        <div className="empty-state">
          <div className="empty-state-icon">🔒</div>
          <h4>No autenticado</h4>
          <p>Inicia sesión para ver tus tareas</p>
        </div>
      </div>
    );
  }

  return (
    <div className="task-box">
      <div className="task-header">
        <h2>To-Do List</h2>
        <button className="add-task-btn" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancelar" : "Add Task"}
        </button>
      </div>

      {showForm && (
        <div className="task-form">
          <input
            type="text"
            placeholder="Describe tu tarea..."
            value={task}
            onChange={(e) => setTask(e.target.value)}
          />
          <div className="input-group">
            <label>Fecha</label>
            <input
              type="date"
              value={date}
              min={today}  // no permitir fecha pasada
              onChange={(e) => setDate(e.target.value)}
              placeholder="Fecha (hoy o futuro)"
              required
            />
            <label>Hora</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="Hora (opcional)"
            />
            <button className="save-btn" onClick={handleAddTask}>Guardar</button>
          </div>
        </div>
      )}

      <div className="tasks-section">
        <h3>Tareas para hoy ({formatDate(today)})</h3>
        {todayTasks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h4>No hay tareas para hoy</h4>
            <p>Añade nuevas tareas con el botón arriba</p>
          </div>
        ) : (
          <ul className="task-list">
            {todayTasks.map(t => (
              <li key={t.id} className={`task-item${t.completed ? " completed" : ""}`}>
                {editingTaskId === t.id ? (
                  <div className="task-form" style={{ marginBottom: 0 }}>
                    <input
                      type="text"
                      value={editTask}
                      onChange={e => setEditTask(e.target.value)}
                      placeholder="Editar tarea..."
                    />
                    <input
                      type="date"
                      value={editDate}
                      min={today}
                      onChange={e => setEditDate(e.target.value)}
                    />
                    <input
                      type="time"
                      value={editTime}
                      onChange={e => setEditTime(e.target.value)}
                    />
                    <button className="save-btn" onClick={() => handleSaveEdit(t.id)}>Guardar</button>
                    <button className="add-task-btn" onClick={() => setEditingTaskId(null)}>Cancelar</button>
                  </div>
                ) : (
                  <>
                    <div className="task-content">
                      <div className="task-text">{t.task}</div>
                      <div className="task-datetime">
                        {formatDate(t.date)} • {formatTime(t.time)}
                      </div>
                    </div>
                    <div className="task-actions">
                      <button
                        className={t.completed ? "undo-btn" : "delete-btn"}
                        onClick={() => handleToggleCompleteTask(t.id, t.completed)}
                      >
                        {t.completed ? "Marcar como no completada" : "Marcar como completada"}
                      </button>
                      <button
                        className="edit-btn"
                        onClick={() => {
                          setEditingTaskId(t.id);
                          setEditTask(t.task);
                          setEditDate(t.date);
                          setEditTime(t.time);
                        }}
                      >
                        Editar
                      </button>
                      {t.completed && <span className="completed-label">Completada</span>}
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ScheduledTaskBox;
